import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Stack,
  Button,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
   InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Key as KeyIcon,
  Mail as MailIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
   Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axios from 'axios';
import { KeyUri, config } from "../key";

export function PasswordReset() {
  const [step, setStep] = useState('email');
  const [activeTab, setActiveTab] = useState('change-password');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
const [showCurrentPassword, setShowCurrentPassword] = useState(false);
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength checker
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const isPasswordStrong = passwordStrength === 4;

 const handleSendCode = async (e) => {
  e.preventDefault();
  setError('');
  if (!email) {
    setError('Please enter your email address');
    return;
  }
  try {
    setLoading(true);
    await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/reset-code`, { email });
    setStep('code');
  } catch (err) {
    setError(err.response?.data?.message || 'Failed to send code');
  } finally {
    setLoading(false);
  }
};

const handleVerifyCode = async (e) => {
  e.preventDefault();
  setError('');
  
 
  const cleanOtp = verificationCode.replace(/[^0-9]/g, ''); 
  if (cleanOtp.length !== 6) {
    setError('Please enter a valid 6-digit code');
    return;
  }
  
  try {
    setLoading(true);
    await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/verify-reset-otp`, { 
      email, 
      otp: cleanOtp 
    });
    setStep('password');
  } catch (err) {
    setError(err.response?.data?.message || 'Invalid OTP');
  } finally {
    setLoading(false);
  }
};

const handleResetPassword = async (e) => {
  e.preventDefault();
  setError('');
  if (newPassword !== confirmPassword) {
    setError('Passwords do not match');
    return;
  }
  try {
    setLoading(true);
     const cleanOtp = verificationCode.replace(/[^0-9]/g, '');
    await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/reset`, { 
      email, 
      otp: cleanOtp, 
      newPassword 
    });
    setStep('success');
    setTimeout(() => setStep('email'), 3000);
  } catch (err) {
    setError(err.response?.data?.message || 'Reset failed');
  } finally {
    setLoading(false);
  }
};

const handleChangePassword = async (e) => {
  e.preventDefault();
  setError('');
  if (newPassword !== confirmPassword) {
    setError('Passwords do not match');
    return;
  }
  try {
    setLoading(true);
     const token = localStorage.getItem('vendorToken');
    if (!token) {
      setError('Please login first');
      return;
    }
    await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/change`, { 
      currentPassword, 
      newPassword 
    },  {
      headers: { 
        Authorization: `Bearer ${token}`  
      }
    });
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('Password changed successfully!');
  } catch (err) {
    setError(err.response?.data?.message || 'Change failed');
  } finally {
    setLoading(false);
  }
};

  const handleResendCode = () => {
    
  };

  const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

const handleMouseDownPassword = (event) => {
  event.preventDefault();
};


  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      {/* Page Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={4}>
        <LockIcon color="primary" />
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Password & Security
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your password and security settings
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 4 }}>
        <CardHeader
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
              <Tabs 
  value={activeTab} 
  onChange={(_, newValue) => setActiveTab(newValue)}
  sx={{ 
    minHeight: 48,
    '& .MuiTabs-indicator': { height: 3 }, 
  }}
  TabIndicatorProps={{
    sx: { 
      height: 3, 
      bgcolor: 'primary.main',
      borderRadius: 1.5 
    }
  }}
>
  <Tab 
    label="Change Password" 
    value="change-password"
    sx={{ 
      minWidth: 180, 
      px: 6, 
      py: 1.5 
    }}
  />
  <Tab 
    label="Reset Password" 
    value="reset-password"
    sx={{ 
      minWidth: 180, 
      px: 6, 
      py: 1.5 
    }}
  />
</Tabs>

            </Box>
          }
        />
        <CardContent sx={{ pt: 3 }}>

          {/* Change Password Tab */}
          {activeTab === 'change-password' && (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <WarningIcon />
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleChangePassword} sx={{ spaceY: 3 }}>
                <Stack spacing={3}>
                 <TextField
  fullWidth
  label="Current Password"
  type={showCurrentPassword ? 'text' : 'password'}
  placeholder="Enter current password"
  value={currentPassword}
  onChange={(e) => setCurrentPassword(e.target.value)}
  required
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowCurrentPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

                <TextField
  fullWidth
  label="New Password"
  type={showNewPassword ? 'text' : 'password'}
  placeholder="Enter new password"
  value={newPassword}
  onChange={(e) => setNewPassword(e.target.value)}
  required
  error={!isPasswordStrong && newPassword.length > 0} 
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowNewPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showNewPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

                 <TextField
  fullWidth
  label="Confirm New Password"
  type={showConfirmPassword ? 'text' : 'password'}
  placeholder="Re-enter new password"
  value={confirmPassword}
  onChange={(e) => setConfirmPassword(e.target.value)}
  required
  error={newPassword !== confirmPassword}
  helperText={newPassword !== confirmPassword ? 'Passwords do not match' : ''}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowConfirmPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

                </Stack>

                {/* Password Requirements */}
                <Card variant="outlined" sx={{ bgcolor: 'grey.50', mt: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Password requirements:
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          size="small"
                          color={newPassword.length >= 8 ? 'success' : 'default'}
                          label="✓"
                        />
                        <Typography variant="body2">At least 8 characters</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          size="small"
                          color={/[A-Z]/.test(newPassword) ? 'success' : 'default'}
                          label="✓"
                        />
                        <Typography variant="body2">One uppercase letter</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          size="small"
                          color={/[0-9]/.test(newPassword) ? 'success' : 'default'}
                          label="✓"
                        />
                        <Typography variant="body2">One number</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          size="small"
                          color={/[^A-Za-z0-9]/.test(newPassword) ? 'success' : 'default'}
                          label="✓"
                        />
                        <Typography variant="body2">One special character</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                  <Button variant="outlined" type="button">Cancel</Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading || !isPasswordStrong}
                    startIcon={loading && <CircularProgress size={20} />}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </Stack>
              </Box>
            </>
          )}

          {/* Reset Password Tab */}
          {activeTab === 'reset-password' && (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <WarningIcon />
                  {error}
                </Alert>
              )}

              {step === 'email' && (
                <Box component="form" onSubmit={handleSendCode}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      placeholder="vendor@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: <MailIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                      required
                    />
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={loading}
                      startIcon={loading && <CircularProgress size={20} />}
                    >
                      {loading ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                  </Stack>
                </Box>
              )}

              {step === 'code' && (
                <Box component="form" onSubmit={handleVerifyCode}>
                  <Stack spacing={3} alignItems="center">
                    <Typography variant="h6" gutterBottom>
                      Verify Your Identity
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Code sent to {email}
                    </Typography>
                    <TextField
                      fullWidth
                      label="Verification Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem' } }}
                      sx={{ maxWidth: 200 }}
                    />
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={loading || verificationCode.length !== 6}
                    >
                      {loading ? 'Verifying...' : 'Verify Code'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={handleResendCode}
                      disabled={loading}
                    >
                      Resend Code
                    </Button>
                  </Stack>
                </Box>
              )}

              {step === 'password' && (
                <Box component="form" onSubmit={handleResetPassword}>
                  <Stack spacing={3}>
                   <TextField
  fullWidth
  label="New Password"
  type={showNewPassword ? 'text' : 'password'}
  value={newPassword}
  onChange={(e) => setNewPassword(e.target.value)}
  required
  error={!isPasswordStrong && newPassword.length > 0}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowNewPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showNewPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

                   <TextField
  fullWidth
  label="Confirm Password"
  type={showConfirmPassword ? 'text' : 'password'}
  value={confirmPassword}
  onChange={(e) => setConfirmPassword(e.target.value)}
  error={newPassword !== confirmPassword}
  helperText={newPassword !== confirmPassword ? 'Passwords do not match' : ''}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowConfirmPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={loading || !isPasswordStrong}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </Stack>
                </Box>
              )}

              {step === 'success' && (
                <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'success.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h5" gutterBottom>
                      Password Reset Complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You can now sign in with your new password
                    </Typography>
                  </Box>
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Security Tips */}
      <Card variant="outlined" sx={{ bgcolor: 'info.light' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <ShieldIcon color="info" sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" color="info.main" gutterBottom>
                Security Tips
              </Typography>
              <Stack spacing={1} component="ul" sx={{ pl: 2, mt: 0.5 }}>
                <Typography variant="body2" color="info.dark">- Use unique passwords</Typography>
                <Typography variant="body2" color="info.dark">- Enable 2FA when available</Typography>
                <Typography variant="body2" color="info.dark">- Never share passwords</Typography>
                <Typography variant="body2" color="info.dark">- Change regularly</Typography>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
